import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';
import { CheckCircle, XCircle, AlertCircle, Maximize2, MonitorSmartphone, ThumbsUp, EyeOff, ListChecks } from 'lucide-react';

const screenshotSrc = (path: string | null): string | null => {
    if (!path) return null;
    if (path.startsWith('data:') || path.startsWith('http')) return path;
    if (path.startsWith('iVBOR') || path.startsWith('/9j/') || path.length > 500) {
        return `data:image/png;base64,${path}`;
    }
    return path;
};

interface EvidenceTabProps {
    entityId: string;
    projectId: string;
}

interface EvidenceRecord {
    id: string;
    session_id: string;
    screen_fingerprint: string;
    screen_name: string;
    evidence_status: string;
    screenshot_path: string | null;
    dom_elements: any;
    created_at: string;
}

const EvidenceTab: React.FC<EvidenceTabProps> = ({ entityId }) => {
    const [records, setRecords] = useState<EvidenceRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<EvidenceRecord | null>(null);
    const [selectedElementIndices, setSelectedElementIndices] = useState<Set<number>>(new Set());
    const [rejectReason] = useState("");

    // Reset selection when changing record
    useEffect(() => {
        const initSelection = async () => {
            if (selectedRecord && Array.isArray(selectedRecord.dom_elements)) {
                // Try to find if we already have a baseline for this screen to recover the previous selection
                // (This is a simplified version, ideally we'd fetch the baseline explicitly)
                const allElements = selectedRecord.dom_elements;
                setSelectedElementIndices(new Set(allElements.map((_: any, i: number) => i)));
            } else {
                setSelectedElementIndices(new Set());
            }
        };
        initSelection();
    }, [selectedRecord]);

    useEffect(() => {
        loadEvidence();
    }, [entityId]);

    const loadEvidence = async () => {
        setLoading(true);
        try {
            const data = await api.getEntityEvidence(entityId);
            setRecords(data.records || []);
        } catch (e) {
            console.error("Error loading relational evidence", e);
        } finally {
            setLoading(false);
        }
    };

    const handleVerify = async (id: string) => {
        try {
            let payload = {};
            
            // If we have a selection for the active record, send only those elements
            if (selectedRecord && selectedRecord.id === id && Array.isArray(selectedRecord.dom_elements)) {
                const filtered = selectedRecord.dom_elements.filter((_: any, i: number) => selectedElementIndices.has(i));
                payload = { dom_elements: filtered };
            }

            await api.verifyEvidence(id, payload);
            await loadEvidence();
            if (selectedRecord?.id === id) setSelectedRecord(null);
        } catch (e) {
            console.error(e);
            alert("Error al verificar la evidencia.");
        }
    };

    const toggleElement = (index: number) => {
        const next = new Set(selectedElementIndices);
        if (next.has(index)) next.delete(index);
        else next.add(index);
        setSelectedElementIndices(next);
    };

    const toggleAll = () => {
        if (!selectedRecord || !Array.isArray(selectedRecord.dom_elements)) return;
        if (selectedElementIndices.size === selectedRecord.dom_elements.length) {
            setSelectedElementIndices(new Set());
        } else {
            setSelectedElementIndices(new Set(selectedRecord.dom_elements.map((_: any, i: number) => i)));
        }
    };

    const handleReject = async (id: string) => {
        const reason = prompt("Razón de rechazo:", rejectReason);
        if (!reason) return;
        
        try {
            await api.rejectEvidence(id, reason);
            await loadEvidence();
            if (selectedRecord?.id === id) setSelectedRecord(null);
        } catch (e) {
            console.error(e);
            alert("Error al rechazar la evidencia.");
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (records.length === 0) {
        return (
            <div className="text-center py-12 border border-dashed border-slate-700 rounded-lg">
                <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MonitorSmartphone className="w-6 h-6 text-slate-500" />
                </div>
                <h3 className="text-slate-300 font-medium mb-2">Sin Evidencias UI</h3>
                <p className="text-slate-500 text-sm max-w-sm mx-auto">
                    Aún no se han capturado evidencias visuales para esta entidad. Ejecuta Discovery interactivo para sincronizar pantallas.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-end border-b border-slate-800 pb-4">
                <div>
                    <h3 className="text-lg font-medium text-white flex items-center gap-2">
                        <MonitorSmartphone className="w-5 h-5 text-blue-400" />
                        Human-Governed QA Review
                    </h3>
                    <p className="text-sm text-slate-400 mt-1">
                        Verifica o rechaza las pantallas capturadas por el bot autónomo.
                    </p>
                </div>
                <div className="text-right">
                    <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Total Pantallas</div>
                    <div className="text-xl font-bold text-white">{records.length}</div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {records.map(record => (
                    <div key={record.id} className={`bg-slate-900 border rounded-xl overflow-hidden transition-colors ${
                        record.evidence_status === 'verified' ? 'border-green-500/30' :
                        record.evidence_status === 'rejected' ? 'border-red-500/30' :
                        'border-slate-800 hover:border-slate-600'
                    }`}>
                        {/* Image Preview */}
                        <div className="h-48 bg-slate-950 relative group">
                            {record.screenshot_path ? (
                                <img 
                                    src={screenshotSrc(record.screenshot_path)!} 
                                    alt={record.screen_name} 
                                    className="w-full h-full object-contain p-2"
                                />
                            ) : (
                                <div className="absolute inset-0 flex items-center justify-center text-slate-600 text-sm">
                                    Sin Screenshot
                                </div>
                            )}
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <button 
                                    onClick={() => setSelectedRecord(record)}
                                    className="p-2 bg-slate-800 rounded-lg text-white hover:bg-slate-700"
                                >
                                    <Maximize2 className="w-5 h-5" />
                                </button>
                            </div>
                            
                            {/* Status Badge */}
                            <div className="absolute top-2 right-2">
                                {record.evidence_status === 'verified' && (
                                    <span className="bg-green-500/20 text-green-400 text-[10px] uppercase font-bold px-2 py-1 rounded border border-green-500/30 flex items-center gap-1">
                                        <CheckCircle className="w-3 h-3" /> Verificado
                                    </span>
                                )}
                                {record.evidence_status === 'rejected' && (
                                    <span className="bg-red-500/20 text-red-400 text-[10px] uppercase font-bold px-2 py-1 rounded border border-red-500/30 flex items-center gap-1">
                                        <XCircle className="w-3 h-3" /> Rechazado
                                    </span>
                                )}
                                {record.evidence_status === 'captured' && (
                                    <span className="bg-blue-500/20 text-blue-400 text-[10px] uppercase font-bold px-2 py-1 rounded border border-blue-500/30 flex items-center gap-1">
                                        <AlertCircle className="w-3 h-3" /> Pendiente
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Metadata */}
                        <div className="p-4">
                            <h4 className="text-white font-medium truncate" title={record.screen_name}>
                                {record.screen_name}
                            </h4>
                            <div className="text-xs text-slate-500 mt-1 flex justify-between">
                                <span>{new Date(record.created_at).toLocaleString()}</span>
                                <span className="font-mono text-[10px]">{record.screen_fingerprint.substring(0,8)}</span>
                            </div>

                            <div className="mt-4 pt-4 border-t border-slate-800 flex justify-between gap-2">
                                <button 
                                    onClick={() => handleReject(record.id)}
                                    disabled={record.evidence_status === 'rejected'}
                                    className="flex-1 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs rounded-lg transition-colors border border-red-500/20 disabled:opacity-50"
                                >
                                    Rechazar
                                </button>
                                <button 
                                    onClick={() => handleVerify(record.id)}
                                    disabled={record.evidence_status === 'verified'}
                                    className="flex-1 px-3 py-1.5 bg-green-500/10 hover:bg-green-500/20 text-green-400 text-xs rounded-lg transition-colors border border-green-500/20 disabled:opacity-50"
                                >
                                    Aprobar
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Detail Modal */}
            {selectedRecord && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
                    <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-5xl max-h-[90vh] flex overflow-hidden">
                        
                        {/* Image Side */}
                        <div className="w-1/2 bg-black flex flex-col">
                            <div className="p-4 border-b border-slate-800 flex justify-between items-center bg-slate-900">
                                <h3 className="text-white font-medium">{selectedRecord.screen_name}</h3>
                            </div>
                            <div className="flex-1 overflow-auto p-4 flex items-center justify-center relative">
                                {selectedRecord.screenshot_path ? (
                                    <img 
                                        src={screenshotSrc(selectedRecord.screenshot_path)!} 
                                        alt={selectedRecord.screen_name} 
                                        className="max-w-full max-h-full object-contain"
                                    />
                                ) : (
                                    <p className="text-slate-500">Imagen no disponible</p>
                                )}
                            </div>
                        </div>

                        {/* DOM Elements Side */}
                        <div className="flex flex-col h-full w-1/2 bg-slate-900 border-l border-slate-800">
                            <div className="p-4 border-b border-slate-800 flex justify-between items-center bg-slate-900/50">
                                <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Elementos Interactivos (DOM)</h3>
                                <div className="flex items-center gap-1">
                                    <button 
                                        onClick={toggleAll}
                                        className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
                                        title="Alternar selección"
                                    >
                                        <ListChecks className="w-5 h-5" />
                                    </button>
                                    <button 
                                        onClick={() => setSelectedRecord(null)}
                                        className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
                                        title="Cerrar"
                                    >
                                        <XCircle className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 space-y-2">
                                {Array.isArray(selectedRecord.dom_elements) ? selectedRecord.dom_elements.map((el: any, i: number) => (
                                    <div 
                                        key={i} 
                                        onClick={() => toggleElement(i)}
                                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                            selectedElementIndices.has(i) 
                                                ? 'bg-blue-500/10 border-blue-500/40 ring-1 ring-blue-500/20' 
                                                : 'bg-slate-950 border-slate-800 opacity-50 grayscale hover:opacity-100 hover:grayscale-0'
                                        }`}
                                    >
                                        <div className="mt-0.5">
                                            {selectedElementIndices.has(i)
                                                ? <ThumbsUp className="w-4 h-4 text-blue-400 fill-blue-400/20" />
                                                : <EyeOff className="w-4 h-4 text-slate-600" />
                                            }
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className={`text-sm font-medium truncate ${selectedElementIndices.has(i) ? 'text-white' : 'text-slate-500 line-through'}`}>
                                                    {el.visibleText || '—'}
                                                </span>
                                                {el.isInteractive && <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
                                                    selectedElementIndices.has(i) ? 'text-blue-400 bg-blue-500/10' : 'text-slate-600 bg-slate-800'
                                                }`}>Interactive</span>}
                                            </div>
                                            <div className="flex items-center gap-2 mt-1">
                                                <code className={`text-[11px] font-mono ${selectedElementIndices.has(i) ? 'text-cyan-400' : 'text-slate-700'}`}>
                                                    {el.elementId}
                                                </code>
                                                <span className="text-[10px] text-slate-600">·</span>
                                                <span className="text-[10px] text-slate-500">{el.elementType}</span>
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <pre className="text-xs text-blue-300 font-mono bg-slate-950 p-4 rounded-lg border border-slate-800 whitespace-pre-wrap">
                                        {JSON.stringify(selectedRecord.dom_elements, null, 2)}
                                    </pre>
                                )}
                            </div>
                            <div className="p-4 border-t border-slate-800 bg-slate-900 flex justify-end gap-3">
                                <button 
                                    onClick={() => handleReject(selectedRecord.id)}
                                    className="px-4 py-2 bg-red-500/10 text-red-400 rounded border border-red-500/20 hover:bg-red-500/20"
                                >
                                    Rechazar
                                </button>
                                <button 
                                    onClick={() => handleVerify(selectedRecord.id)}
                                    className="px-4 py-2 bg-green-500/10 text-green-400 rounded border border-green-500/20 hover:bg-green-500/20"
                                >
                                    Aprobar (Baseline)
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EvidenceTab;
